import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  TrendingUp, Users, FileText, Star, MapPin, 
  RefreshCw, Loader2, Send, Copy, MessageSquare,
  Calendar, PoundSterling, Clock, ChevronRight,
  Sparkles, Phone, Mail, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatDate } from "@/lib/constants";

const API = `${window.location.origin}/api`;

export const GrowthOpportunitiesPanel = ({ authHeaders }) => {
  const [opportunities, setOpportunities] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  
  // Message generation state
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [messageType, setMessageType] = useState("");
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [generatingMessage, setGeneratingMessage] = useState(false);

  const fetchOpportunities = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      const response = await axios.get(`${API}/admin/opportunities`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpportunities(response.data);
    } catch (err) {
      console.error("Error fetching opportunities:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      fetchOpportunities();
    }
  }, []);

  const generateMessage = async (type, data) => {
    setGeneratingMessage(true);
    setGeneratedMessage("");
    
    try {
      let endpoint = "";
      let payload = {};
      
      switch (type) {
        case "re-engagement":
          endpoint = "/admin/ai/re-engagement-message";
          payload = {
            customer_name: data.customer_name,
            last_job_description: data.last_job_description,
            days_since_last_job: data.days_since_last_job
          };
          break;
        case "quote-followup":
          endpoint = "/admin/ai/quote-followup-message";
          payload = {
            customer_name: data.customer_name,
            quote_value: data.total,
            days_waiting: data.days_waiting,
            job_description: data.job_description
          };
          break;
        case "review-request":
          endpoint = "/admin/ai/review-request";
          payload = {
            customer_name: data.customer_name,
            service_type: data.service,
            job_description: data.job_description
          };
          break;
        case "nearby":
          endpoint = "/admin/ai/nearby-notification";
          payload = {
            customer_name: data.customer_name,
            area: data.address,
            scheduled_date: data.scheduled_date
          };
          break;
        default:
          throw new Error("Unknown message type");
      }
      
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedMessage(response.data.message || response.data.reply || "");
      toast.success("Message generated!");
    } catch (error) {
      console.error("Error generating message:", error);
      toast.error("Failed to generate message");
    } finally {
      setGeneratingMessage(false);
    }
  };

  const openMessageDialog = (type, opportunity) => {
    setSelectedOpportunity(opportunity);
    setMessageType(type);
    setGeneratedMessage("");
    setMessageDialogOpen(true);
    generateMessage(type, opportunity);
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(generatedMessage);
    toast.success("Message copied to clipboard!");
  };

  const totalOpportunities = opportunities?.summary?.total_opportunities || 0;

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2 text-emerald-800">
              <TrendingUp className="h-5 w-5" />
              Growth Opportunities
            </CardTitle>
            <CardDescription>Where is your next job coming from?</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {totalOpportunities > 0 && (
              <Badge className="bg-emerald-500 text-white">{totalOpportunities} opportunities</Badge>
            )}
            <Button variant="outline" size="sm" onClick={fetchOpportunities} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && !opportunities ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>
        ) : error && !opportunities ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertCircle className="h-10 w-10 mx-auto mb-2 text-amber-400" />
            <p className="text-sm">Couldn't load opportunities</p>
            <Button variant="outline" size="sm" onClick={fetchOpportunities} className="mt-2 gap-1">
              <RefreshCw className="h-3 w-3" /> Retry
            </Button>
          </div>
        ) : !opportunities || totalOpportunities === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No opportunities detected</p>
            <p className="text-sm">Check back after more customer activity</p>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-5 mb-4">
              <TabsTrigger value="all" className="text-xs">
                All ({totalOpportunities})
              </TabsTrigger>
              <TabsTrigger value="repeat" className="text-xs">
                <Users className="h-3 w-3 mr-1" />
                Repeat ({opportunities.summary.repeat_customers_count})
              </TabsTrigger>
              <TabsTrigger value="quotes" className="text-xs">
                <FileText className="h-3 w-3 mr-1" />
                Quotes ({opportunities.summary.quote_followups_count})
              </TabsTrigger>
              <TabsTrigger value="reviews" className="text-xs">
                <Star className="h-3 w-3 mr-1" />
                Reviews ({opportunities.summary.review_opportunities_count})
              </TabsTrigger>
              <TabsTrigger value="nearby" className="text-xs">
                <MapPin className="h-3 w-3 mr-1" />
                Nearby ({opportunities.summary.nearby_opportunities_count})
              </TabsTrigger>
            </TabsList>

            {/* All Opportunities View */}
            <TabsContent value="all">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {/* Priority: Quote follow-ups (money waiting) */}
                  {opportunities.quote_followups.slice(0, 3).map((opp, idx) => (
                    <OpportunityCard
                      key={`quote-${idx}`}
                      type="quote"
                      icon={<FileText className="h-4 w-4" />}
                      title={opp.customer_name}
                      subtitle={`Quote: £${opp.total?.toLocaleString() || 0}`}
                      badge={`${opp.days_waiting} days waiting`}
                      badgeColor="bg-amber-100 text-amber-800"
                      onAction={() => openMessageDialog("quote-followup", opp)}
                      actionLabel="Follow Up"
                      phone={opp.customer_phone}
                      email={opp.customer_email}
                    />
                  ))}
                  
                  {/* Review opportunities */}
                  {opportunities.review_opportunities.slice(0, 3).map((opp, idx) => (
                    <OpportunityCard
                      key={`review-${idx}`}
                      type="review"
                      icon={<Star className="h-4 w-4" />}
                      title={opp.customer_name}
                      subtitle={opp.service?.replace(/-/g, ' ')}
                      badge="No review yet"
                      badgeColor="bg-purple-100 text-purple-800"
                      onAction={() => openMessageDialog("review-request", opp)}
                      actionLabel="Request Review"
                      phone={opp.phone}
                      email={opp.email}
                    />
                  ))}
                  
                  {/* Repeat customers */}
                  {opportunities.repeat_customers.slice(0, 3).map((opp, idx) => (
                    <OpportunityCard
                      key={`repeat-${idx}`}
                      type="repeat"
                      icon={<Users className="h-4 w-4" />}
                      title={opp.customer_name}
                      subtitle={`Last job: ${formatDate(opp.last_job_date)}`}
                      badge={`${Math.floor(opp.days_since_last_job / 30)}+ months ago`}
                      badgeColor="bg-blue-100 text-blue-800"
                      onAction={() => openMessageDialog("re-engagement", opp)}
                      actionLabel="Re-engage"
                      phone={opp.phone}
                      email={opp.email}
                      extra={opp.total_spent > 0 ? `Spent: £${opp.total_spent.toLocaleString()}` : null}
                    />
                  ))}
                  
                  {/* Nearby opportunities */}
                  {opportunities.nearby_customers.slice(0, 2).map((opp, idx) => (
                    <NearbyOpportunityCard
                      key={`nearby-${idx}`}
                      scheduledJob={opp.scheduled_job}
                      nearbyCustomers={opp.nearby_customers}
                      onAction={(customer) => openMessageDialog("nearby", {
                        customer_name: customer.customer_name,
                        address: customer.address,
                        scheduled_date: opp.scheduled_job.date
                      })}
                    />
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Repeat Customers Tab */}
            <TabsContent value="repeat">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {opportunities.repeat_customers.length === 0 ? (
                    <EmptyState icon={Users} message="No dormant customers found" />
                  ) : (
                    opportunities.repeat_customers.map((opp, idx) => (
                      <OpportunityCard
                        key={idx}
                        type="repeat"
                        icon={<Users className="h-4 w-4" />}
                        title={opp.customer_name}
                        subtitle={`Last: ${opp.last_job_description || 'Previous work'}`}
                        badge={`${Math.floor(opp.days_since_last_job / 30)} months ago`}
                        badgeColor="bg-blue-100 text-blue-800"
                        onAction={() => openMessageDialog("re-engagement", opp)}
                        actionLabel="Re-engage"
                        phone={opp.phone}
                        email={opp.email}
                        extra={opp.total_spent > 0 ? `Lifetime: £${opp.total_spent.toLocaleString()}` : null}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Quote Follow-ups Tab */}
            <TabsContent value="quotes">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {opportunities.quote_followups.length === 0 ? (
                    <EmptyState icon={FileText} message="No quotes awaiting response" />
                  ) : (
                    opportunities.quote_followups.map((opp, idx) => (
                      <OpportunityCard
                        key={idx}
                        type="quote"
                        icon={<PoundSterling className="h-4 w-4" />}
                        title={opp.customer_name}
                        subtitle={`Quote ${opp.reference}: £${opp.total?.toLocaleString() || 0}`}
                        badge={`${opp.days_waiting} days`}
                        badgeColor="bg-amber-100 text-amber-800"
                        onAction={() => openMessageDialog("quote-followup", opp)}
                        actionLabel="Follow Up"
                        phone={opp.customer_phone}
                        email={opp.customer_email}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Reviews Tab */}
            <TabsContent value="reviews">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {opportunities.review_opportunities.length === 0 ? (
                    <EmptyState icon={Star} message="All completed jobs have reviews requested" />
                  ) : (
                    opportunities.review_opportunities.map((opp, idx) => (
                      <OpportunityCard
                        key={idx}
                        type="review"
                        icon={<Star className="h-4 w-4" />}
                        title={opp.customer_name}
                        subtitle={opp.service?.replace(/-/g, ' ').replace(/_/g, ' ')}
                        badge={opp.status}
                        badgeColor="bg-purple-100 text-purple-800"
                        onAction={() => openMessageDialog("review-request", opp)}
                        actionLabel="Request Review"
                        phone={opp.phone}
                        email={opp.email}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Nearby Tab */}
            <TabsContent value="nearby">
              <ScrollArea className="h-[400px] pr-4">
                <div className="space-y-3">
                  {opportunities.nearby_customers.length === 0 ? (
                    <EmptyState icon={MapPin} message="No nearby customer opportunities" />
                  ) : (
                    opportunities.nearby_customers.map((opp, idx) => (
                      <NearbyOpportunityCard
                        key={idx}
                        scheduledJob={opp.scheduled_job}
                        nearbyCustomers={opp.nearby_customers}
                        onAction={(customer) => openMessageDialog("nearby", {
                          customer_name: customer.customer_name,
                          address: customer.address,
                          scheduled_date: opp.scheduled_job.date
                        })}
                      />
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}

        {/* Potential Value Summary */}
        {opportunities?.summary?.potential_value > 0 && (
          <div className="mt-4 p-3 bg-emerald-100/50 rounded-lg text-center">
            <p className="text-sm text-emerald-700">
              <PoundSterling className="h-4 w-4 inline mr-1" />
              <span className="font-bold">£{opportunities.summary.potential_value.toLocaleString()}</span> in pending quotes
            </p>
          </div>
        )}
      </CardContent>

      {/* Message Generation Dialog */}
      <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              AI Message Generator
            </DialogTitle>
            <DialogDescription>
              {selectedOpportunity?.customer_name && (
                <span>Message for {selectedOpportunity.customer_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {generatingMessage ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-500 mr-2" />
                <span className="text-muted-foreground">Generating message...</span>
              </div>
            ) : generatedMessage ? (
              <>
                <Textarea
                  value={generatedMessage}
                  onChange={(e) => setGeneratedMessage(e.target.value)}
                  className="min-h-[150px]"
                  placeholder="Generated message will appear here..."
                />
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={copyMessage} variant="outline" className="gap-1">
                    <Copy className="h-4 w-4" /> Copy
                  </Button>
                  <Button 
                    onClick={() => generateMessage(messageType, selectedOpportunity)} 
                    variant="outline" 
                    className="gap-1"
                  >
                    <RefreshCw className="h-4 w-4" /> Regenerate
                  </Button>
                  {selectedOpportunity?.phone && (
                    <Button 
                      variant="outline" 
                      className="gap-1"
                      onClick={() => {
                        window.open(`https://wa.me/${selectedOpportunity.phone.replace(/\s/g, '')}?text=${encodeURIComponent(generatedMessage)}`, '_blank');
                      }}
                    >
                      <MessageSquare className="h-4 w-4" /> WhatsApp
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Failed to generate message</p>
                <Button 
                  onClick={() => generateMessage(messageType, selectedOpportunity)} 
                  variant="outline" 
                  className="mt-2"
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// Opportunity Card Component
const OpportunityCard = ({ type, icon, title, subtitle, badge, badgeColor, onAction, actionLabel, phone, email, extra }) => (
  <div className="p-3 border rounded-lg hover:bg-muted/30 transition-colors">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className={`p-2 rounded-lg ${
          type === 'quote' ? 'bg-amber-100 text-amber-700' :
          type === 'review' ? 'bg-purple-100 text-purple-700' :
          type === 'repeat' ? 'bg-blue-100 text-blue-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{title}</p>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          {extra && <p className="text-xs text-emerald-600 font-medium">{extra}</p>}
          <div className="flex items-center gap-2 mt-1">
            {phone && (
              <a href={`tel:${phone}`} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
                <Phone className="h-3 w-3" /> {phone}
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 flex-shrink-0">
        <Badge className={badgeColor}>{badge}</Badge>
        <Button size="sm" onClick={onAction} className="gap-1">
          <Send className="h-3 w-3" /> {actionLabel}
        </Button>
      </div>
    </div>
  </div>
);

// Nearby Opportunity Card Component
const NearbyOpportunityCard = ({ scheduledJob, nearbyCustomers, onAction }) => (
  <div className="p-3 border rounded-lg border-green-200 bg-green-50/30">
    <div className="flex items-center gap-2 mb-2">
      <MapPin className="h-4 w-4 text-green-600" />
      <span className="font-medium text-sm">Job in {scheduledJob.address?.split(',')[0] || 'area'}</span>
      <Badge variant="outline" className="text-xs">{formatDate(scheduledJob.date)}</Badge>
    </div>
    <p className="text-xs text-muted-foreground mb-2">
      {scheduledJob.customer_name} - {scheduledJob.service?.replace(/-/g, ' ')}
    </p>
    <div className="space-y-1">
      <p className="text-xs font-medium text-green-700">Nearby past customers:</p>
      {nearbyCustomers.slice(0, 3).map((customer, idx) => (
        <div key={idx} className="flex items-center justify-between text-xs p-1.5 bg-white rounded">
          <span className="truncate flex-1">{customer.customer_name} ({customer.distance_miles} mi)</span>
          <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => onAction(customer)}>
            <Send className="h-3 w-3 mr-1" /> Notify
          </Button>
        </div>
      ))}
    </div>
  </div>
);

// Empty State Component
const EmptyState = ({ icon: Icon, message }) => (
  <div className="text-center py-8 text-muted-foreground">
    <Icon className="h-10 w-10 mx-auto mb-2 opacity-30" />
    <p className="text-sm">{message}</p>
  </div>
);

export default GrowthOpportunitiesPanel;
